import {
  AlertTriangle,
  Calendar,
  Clock,
  Edit,
  MoreHorizontal,
  Plus,
  Settings,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  useConvertTemplateToCustomPlan,
  useDeleteWorkerPlan,
} from "@/domains/shifts/hooks/use-shift-mutations";
import { useWorkerPlanQuery } from "@/domains/shifts/hooks/use-shift-queries";
import type { Worker } from "@/gql/graphql";
import { EditWorkerPlanDialog } from "./dialogs/edit-worker-plan-dialog";

interface WorkerPlanCardProps {
  worker: Worker;
  workerSalonId?: string;
  onCreatePlan: (workerSalonId: string) => void;
  templates: any[];
}

export function WorkerPlanCard({
  worker,
  workerSalonId,
  onCreatePlan,
  templates,
}: WorkerPlanCardProps) {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Fetch worker plan data
  const {
    plan,
    loading: planLoading,
    refetch: refetchPlan,
  } = useWorkerPlanQuery({
    workerSalonId: workerSalonId || "",
    skip: !workerSalonId,
  });

  const { execute: deletePlan, isLoading: deleteLoading } = useDeleteWorkerPlan();
  const { execute: convertToCustom } = useConvertTemplateToCustomPlan();

  if (!workerSalonId) {
    return (
      <Card className="border-dashed opacity-60">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Avatar className="w-10 h-10">
              <AvatarImage src={worker.profileImageUrl ?? undefined} />
              <AvatarFallback>
                {worker.user?.firstName?.[0] || "U"}
                {worker.user?.lastName?.[0] || "N"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h3 className="font-medium text-sm">
                {worker.user?.firstName} {worker.user?.lastName}
              </h3>
              <p className="text-xs text-muted-foreground">Not assigned to this salon</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const handleDeletePlan = async () => {
    if (!plan?.id) return;

    try {
      await deletePlan(plan.id);
      await refetchPlan();
      setIsDeleteDialogOpen(false);
    } catch (error) {
      console.error("Failed to delete plan:", error);
    }
  };

  const handleConvertToCustom = async () => {
    if (!plan?.id) return;

    try {
      await convertToCustom(plan.id);
      await refetchPlan();
    } catch (error) {
      console.error("Failed to convert to custom plan:", error);
    }
  };

  const getDayNames = (days: number[]) => {
    const dayNames = ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    return days.map((d) => dayNames[d]).join(", ");
  };

  const getTimeRange = (plan: any) => {
    if (plan?.planType === "TEMPLATE" && plan?.template?.timeIntervals) {
      const intervals = plan.template.timeIntervals;
      if (intervals.length > 0) {
        const firstStart = intervals[0].startTime;
        const lastEnd = intervals[intervals.length - 1].endTime;
        return `${firstStart} - ${lastEnd}`;
      }
    } else if (plan?.planType === "CUSTOM" && plan?.customTimeIntervals) {
      const intervals = plan.customTimeIntervals;
      if (intervals.length > 0) {
        const firstStart = intervals[0].startTime;
        const lastEnd = intervals[intervals.length - 1].endTime;
        return `${firstStart} - ${lastEnd}`;
      }
    }
    return "Not specified";
  };

  const getScheduleDays = (plan: any) => {
    if (plan?.planType === "TEMPLATE" && plan?.template?.days) {
      return getDayNames(plan.template.days);
    }
    if (plan?.planType === "CUSTOM" && plan?.customDays) {
      return getDayNames(plan.customDays);
    }
    return "Not specified";
  };

  return (
    <>
      <Card className={`transition-colors ${plan ? "" : "border-dashed"}`}>
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            {/* Worker Info */}
            <div className="flex items-center gap-3 flex-1">
              <Avatar className="w-10 h-10">
                <AvatarImage src={worker.profileImageUrl ?? undefined} />
                <AvatarFallback>
                  {worker.user?.firstName?.[0] || "U"}
                  {worker.user?.lastName?.[0] || "N"}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-medium text-sm truncate">
                    {worker.user?.firstName} {worker.user?.lastName}
                  </h3>
                  {planLoading && <div className="w-4 h-4 bg-muted animate-pulse rounded" />}
                </div>

                <p className="text-xs text-muted-foreground truncate mb-2">{worker.user?.email}</p>

                {/* Plan Status */}
                {plan ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      {plan.planType === "TEMPLATE" ? (
                        <>
                          <Clock className="w-3 h-3 text-blue-500" />
                          <Badge
                            variant="outline"
                            className="text-xs px-2 py-0 bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-950/50 dark:border-blue-800 dark:text-blue-300"
                          >
                            Template
                          </Badge>
                          {plan.template && (
                            <span className="text-xs text-muted-foreground">
                              {plan.template.name}
                            </span>
                          )}
                        </>
                      ) : (
                        <>
                          <Settings className="w-3 h-3 text-orange-500" />
                          <Badge
                            variant="outline"
                            className="text-xs px-2 py-0 bg-orange-50 border-orange-200 text-orange-700 dark:bg-orange-950/50 dark:border-orange-800 dark:text-orange-300"
                          >
                            Custom
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {plan.customScheduleType}
                          </span>
                        </>
                      )}
                    </div>

                    {/* Schedule Summary */}
                    <div className="text-xs text-muted-foreground space-y-1">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        <span>{getScheduleDays(plan)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>{getTimeRange(plan)}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-3 h-3 text-amber-500" />
                    <Badge
                      variant="outline"
                      className="text-xs px-2 py-0 bg-amber-50 border-amber-200 text-amber-700 dark:bg-amber-950/50 dark:border-amber-800 dark:text-amber-300"
                    >
                      Unassigned
                    </Badge>
                    <span className="text-xs text-muted-foreground">No schedule assigned</span>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {plan ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => setIsEditDialogOpen(true)}>
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Plan
                    </DropdownMenuItem>

                    {plan.planType === "TEMPLATE" && (
                      <DropdownMenuItem onClick={handleConvertToCustom}>
                        <Settings className="w-4 h-4 mr-2" />
                        Convert to Custom
                      </DropdownMenuItem>
                    )}

                    <DropdownMenuSeparator />

                    <DropdownMenuItem
                      onClick={() => setIsDeleteDialogOpen(true)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Remove Plan
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onCreatePlan(workerSalonId)}
                  className="text-xs"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Assign
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <EditWorkerPlanDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        plan={plan}
        worker={worker}
        templates={templates}
        onSuccess={() => {
          refetchPlan();
          setIsEditDialogOpen(false);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Worker Plan</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove the schedule plan for {worker.user?.firstName}{" "}
              {worker.user?.lastName}? This will delete all future shifts and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePlan}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteLoading}
            >
              {deleteLoading ? "Removing..." : "Remove Plan"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
